import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { checkPaymentLinkingStatus } from "../../redux/slices/paymentLinkingSlice";

/**
 * Hook to poll payment status at regular intervals
 * @param {string} linkCode - Payment link code
 * @param {string} transactionId - Transaction ID to check
 * @param {boolean} enabled - Whether polling is enabled
 * @param {number} interval - Polling interval in milliseconds (default: 3000ms)
 * @param {function} onStatusChange - Callback when status changes
 */
const usePaymentStatusChecker = (
  linkCode,
  transactionId,
  enabled = false,
  interval = 3000,
  onStatusChange = null,
) => {
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  const previousStatusRef = useRef(null);

  useEffect(() => {
    if (!enabled || !linkCode || !transactionId) {
      return;
    }

    const checkStatus = async () => {
      try {
        const result = await dispatch(
          checkPaymentLinkingStatus({ linkCode, transactionId }),
        ).unwrap();

        // Call callback if status changed
        if (onStatusChange && result.status !== previousStatusRef.current) {
          onStatusChange(result.status, result);
          previousStatusRef.current = result.status;
        }

        // Stop polling if terminal status reached
        if (["success", "failed", "no_transaction"].includes(result.status)) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (error) {
        console.error("Failed to check payment status:", error);

        // Stop polling after multiple consecutive errors
        if (onStatusChange) {
          onStatusChange("error", { error: error.message || "Failed to check payment status" });
        }

        // Don't stop polling immediately - might be temporary network issue
        // But log the error for debugging
      }
    };

    // Initial check
    checkStatus();

    // Set up polling
    intervalRef.current = setInterval(checkStatus, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [linkCode, transactionId, enabled, interval, dispatch, onStatusChange]);

  return null;
};

export default usePaymentStatusChecker;
